import React, { useMemo } from 'react';
import { Box, Typography, Modal, Button, Badge, Loader, Table } from '@lucid/lego';
import type { TableColumn } from '@lucid/lego';
import { useGetVehicleOrderComparison } from '../../../../../api/services/lupp/order/importer/importer';
import type {
  ComparisonSection,
  ComparisonValue,
} from '../../../../../api/services/lupp/order/importer/dto.importer';
import styles from './ComparisonModal.module.scss';

export type ComparisonModalProps = {
  dataTestId: string;
  isOpen: boolean;
  onClose: () => void;
  orderNumber: string | null;
  orderLineNumber: string | null;
  onAccept: () => void;
  onReject: () => void;
  isActionPending?: boolean;
  theme?: 'light' | 'dark';
  labels?: Record<string, string>;
};

type ComparisonRow =
  | { rowType: 'section'; key: string; label: string }
  | {
      rowType: 'attribute';
      key: string;
      label: string;
      changed: boolean;
      baseline: ComparisonValue;
      proposed: ComparisonValue;
    };

const buildRows = (sections: ComparisonSection[]): ComparisonRow[] =>
  sections.flatMap((section, sectionIndex) => {
    const items = section.attributes ?? section.rows ?? [];
    return [
      {
        rowType: 'section' as const,
        key: `section-${sectionIndex}`,
        label: section.sectionLabel.split('_').join(' '),
      },
      ...items.map((item, itemIndex) => ({
        rowType: 'attribute' as const,
        key: `attribute-${sectionIndex}-${itemIndex}`,
        label: item.label,
        changed: item.changed,
        baseline: item.baseline,
        proposed: item.proposed,
      })),
    ];
  });

const LEADING_NUMBER_PATTERN = /-?\d+(\.\d+)?/;

const parseLeadingNumber = (text: string): number | null => {
  const match = LEADING_NUMBER_PATTERN.exec(text);
  return match ? Number(match[0]) : null;
};

// Green when the proposed value is more favorable (lower price/shorter wait), red when worse.
const getProposedColor = (row: Extract<ComparisonRow, { rowType: 'attribute' }>) => {
  if (!row.changed) return undefined;
  const baselineNum = row.baseline.value ?? parseLeadingNumber(row.baseline.name);
  const proposedNum = row.proposed.value ?? parseLeadingNumber(row.proposed.name);
  if (baselineNum === null || proposedNum === null || baselineNum === proposedNum) return undefined;
  return proposedNum < baselineNum ? 'success-1' : 'error-1';
};

const formatPrice = (value: ComparisonValue): string | null => {
  if (value.value === null || value.value === undefined) return null;
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: value.currency ?? 'USD',
  }).format(value.value);
};

const isModelRow = (row: ComparisonRow) =>
  row.rowType === 'attribute' && row.label.trim().toLowerCase() === 'model';

/**
 * Shows the baseline vs proposed attribute comparison for a vehicle order awaiting
 * confirmation, with Accept/Reject actions in the footer.
 * @component
 */
const ComparisonModal: React.FC<ComparisonModalProps> = ({
  dataTestId,
  isOpen,
  onClose,
  orderNumber,
  orderLineNumber,
  onAccept,
  onReject,
  isActionPending = false,
  theme = 'dark',
  labels,
}) => {
  const {
    data: comparison,
    isLoading,
    error,
  } = useGetVehicleOrderComparison({
    orderNumber,
    orderLineNumber,
    enabled: isOpen && !!orderNumber && !!orderLineNumber,
  });

  const rows = useMemo(() => buildRows(comparison?.sections ?? []), [comparison]);

  const columns: TableColumn<ComparisonRow>[] = useMemo(
    () => [
      {
        key: 'label',
        title: labels?.['comparisonModal.attribute'] ?? 'Attribute',
        width: '24%',
        render: (_value, row) =>
          row.rowType === 'section' ? (
            <Typography variant="label" size="small" color="gray-3" theme={theme}>
              {row.label}
            </Typography>
          ) : (
            <Box className={styles.attributeCell}>
              <Typography variant="body" size="small" color="white" theme={theme}>
                {row.label}
              </Typography>
              {row.changed && (
                <Badge
                  label={labels?.['comparisonModal.changed'] ?? 'Changed'}
                  color="informative"
                  theme={theme}
                />
              )}
            </Box>
          ),
      },
      {
        key: 'baseline',
        title: labels?.['comparisonModal.baseline'] ?? 'Baseline Configuration',
        width: '38%',
        render: (_value, row) =>
          row.rowType === 'attribute' ? (
            <Box className={styles.valueCell}>
              {!isModelRow(row) && formatPrice(row.baseline) && (
                <Typography
                  variant="body"
                  size="small"
                  color="gray-3"
                  theme={theme}
                  className={styles.valuePrice}
                >
                  {formatPrice(row.baseline)}
                </Typography>
              )}
              <Typography
                variant="body"
                size="small"
                color="gray-3"
                theme={theme}
                className={styles.valueName}
              >
                {row.baseline.name}
              </Typography>
            </Box>
          ) : null,
      },
      {
        key: 'proposed',
        title: labels?.['comparisonModal.proposed'] ?? 'Proposed Configuration',
        width: '38%',
        render: (_value, row) => {
          if (row.rowType !== 'attribute') return null;
          const color = getProposedColor(row) ?? 'white';
          return (
            <Box className={styles.valueCell}>
              {!isModelRow(row) && formatPrice(row.proposed) && (
                <Typography
                  variant="body"
                  size="small"
                  color={color}
                  theme={theme}
                  className={styles.valuePrice}
                >
                  {formatPrice(row.proposed)}
                </Typography>
              )}
              <Typography
                variant="body"
                size="small"
                color={color}
                theme={theme}
                className={styles.valueName}
              >
                {row.proposed.name}
              </Typography>
            </Box>
          );
        },
      },
    ],
    [labels, theme]
  );

  return (
    <Modal
      dataTestId={dataTestId}
      isOpen={isOpen}
      onClose={onClose}
      title={labels?.['comparisonModal.title'] ?? 'Baseline vs Proposed Match'}
      theme={theme}
      size="large"
      className={styles.wideModal}
      closeOnOverlayClick={!isActionPending}
      footer={
        <Box className={styles.footer}>
          <Typography variant="body" size="small" color="gray-3" theme={theme}>
            {labels?.['comparisonModal.disclaimer'] ??
              'Accepting this proposal will persist it as the new baseline for future matching and vehicle swaps.'}
          </Typography>
          <Box className={styles.footerActions}>
            <Button
              label={labels?.['notifications.reject'] ?? 'Reject'}
              type="secondary"
              color="neutral"
              theme={theme}
              size="medium"
              isFluid
              handleClick={onReject}
              disabled={isActionPending}
              dataTestId={`${dataTestId}-reject-button`}
              gaComponent="order-notifications-comparison"
            />
            <Button
              label={labels?.['notifications.accept'] ?? 'Accept'}
              type="primary"
              color="neutral"
              theme={theme}
              size="medium"
              isFluid
              handleClick={onAccept}
              disabled={isActionPending}
              dataTestId={`${dataTestId}-accept-button`}
              gaComponent="order-notifications-comparison"
            />
          </Box>
        </Box>
      }
    >
      <Typography
        variant="subtitle"
        size="small"
        color="white"
        theme={theme}
        className={styles.orderId}
      >
        {(labels?.['comparisonModal.orderId'] ?? 'Order ID:') + ' '}
        {comparison?.vehicleOrderNumber ?? orderLineNumber}
      </Typography>

      {isLoading && (
        <Box className={styles.loaderContainer}>
          <Loader size={32} theme={theme} />
        </Box>
      )}

      {!isLoading && error && (
        <Typography variant="body" size="small" color="error-1" theme={theme}>
          {labels?.['comparisonModal.loadError'] ?? 'Unable to load comparison details.'}
        </Typography>
      )}

      {!isLoading && !error && comparison && (
        <Table
          columns={columns}
          data={rows}
          theme={theme}
          className={styles.comparisonTable}
          dataTestId={`${dataTestId}-table`}
          pagination={{ enabled: false }}
          selection={{ enabled: false }}
          compact
        />
      )}
    </Modal>
  );
};

export default ComparisonModal;
